import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Chip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/services/api';
import PageHeader from '@/components/Layout/PageHeader';
import SessionFormDialog, { type SessionFormValues } from '@/components/SessionFormDialog';
import ReviewSessionSearchBar, {
  emptyReviewFilters,
  type ReviewSearchFilters,
} from '@/components/review/ReviewSessionSearchBar';
import {
  ReviewSessionCard,
  ReviewSessionTable,
  reviewSessionPath,
} from '@/components/review/ReviewSessionListItems';
import type { ReviewSession } from '@/types';

export default function ReviewListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [sessions, setSessions] = useState<ReviewSession[]>([]);
  const [filters, setFilters] = useState<ReviewSearchFilters>(emptyReviewFilters);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = {
      q: filters.q,
      team: filters.team,
      competition: filters.competition,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      filter: filters.filter,
    };
    api
      .get<{ sessions: ReviewSession[] }>('/review-sessions', { params })
      .then((res) => {
        setSessions(res.data.sessions);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (values: SessionFormValues) => {
    const res = await api.post<{ session: ReviewSession }>('/review-sessions', values);
    setDialogOpen(false);
    navigate(`/review/${res.data.session.id}`);
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('確定刪除此分析場次？')) return;
    await api.delete(`/review-sessions/${id}`);
    load();
  };

  const emptyState = useMemo(
    () => (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="text.secondary" gutterBottom>
          {loading ? '載入中…' : '找不到符合條件的影片分析'}
        </Typography>
        {!loading && (
          <Button variant="outlined" color="secondary" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ mt: 1 }}>
            新增第一個分析
          </Button>
        )}
      </Box>
    ),
    [loading]
  );

  const showEmpty = !loading && sessions.length === 0;

  return (
    <Box>
      <PageHeader
        icon={<PlayCircleOutlineIcon />}
        title="影片分析"
        subtitle="所有比賽影片分析場次（包含分享給您的場次）"
        chips={
          !loading ? (
            <Chip size="small" color="secondary" variant="outlined" label={`${sessions.length} 場`} />
          ) : undefined
        }
        actions={
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            新增分析
          </Button>
        }
      />

      <ReviewSessionSearchBar
        filters={filters}
        onChange={setFilters}
        onSearch={load}
        resultCount={loading ? undefined : sessions.length}
      />

      {showEmpty ? (
        emptyState
      ) : isMobile ? (
        <Stack spacing={1.25}>
          {loading ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              載入中…
            </Typography>
          ) : (
            sessions.map((s) => (
              <ReviewSessionCard key={s.id} session={s} onDelete={handleDelete} />
            ))
          )}
        </Stack>
      ) : (
        <ReviewSessionTable
          sessions={sessions}
          loading={loading}
          onRowClick={(s) => navigate(reviewSessionPath(s))}
          onDelete={handleDelete}
        />
      )}

      <SessionFormDialog open={dialogOpen} title="新增影片分析" showYoutube onClose={() => setDialogOpen(false)} onSubmit={handleCreate} />
    </Box>
  );
}
